import { useCallback, useMemo, useState } from 'react';
import type { StatementPeriod } from '../types/dealerFundStatementPeriod.types';
import { monthOptionLabel } from '../utils/fundStatementLabels';
import {
    periodKey,
    previousMonth,
    recentMonths,
    STATEMENT_MONTH_OPTIONS,
    validateRange,
} from '../utils/statementPeriod';
import { useUtcToday } from './useUtcToday';

export type StatementPeriodMode = 'month' | 'range';

export interface RangeDraft {
    from: string | null;
    to: string | null;
}

const EMPTY_DRAFT: RangeDraft = { from: null, to: null };

const initialRange = (initial: StatementPeriod | null | undefined, rangeAllowed: boolean) =>
    initial?.kind === 'range' && rangeAllowed ? { from: initial.from, to: initial.to } : null;

export function useStatementPeriod(rangeAllowed: boolean, now?: () => Date, initial?: StatementPeriod | null) {
    const todayIso = useUtcToday(now);
    const [seededRange] = useState(() => initialRange(initial, rangeAllowed));
    const [mode, setModeState] = useState<StatementPeriodMode>(seededRange ? 'range' : 'month');
    const [month, setMonth] = useState(() => (initial?.kind === 'month' ? initial.month : previousMonth(todayIso)));
    const [rangeDraft, setRangeDraft] = useState<RangeDraft>(seededRange ?? EMPTY_DRAFT);
    const [appliedRange, setAppliedRange] = useState<{ from: string; to: string } | null>(seededRange);
    const [lastRangeAllowed, setLastRangeAllowed] = useState(rangeAllowed);

    if (lastRangeAllowed !== rangeAllowed) {
        setLastRangeAllowed(rangeAllowed);
        if (!rangeAllowed) {
            setModeState('month');
            setAppliedRange(null);
        }
    }

    const monthOptions = useMemo(
        () => recentMonths(todayIso, STATEMENT_MONTH_OPTIONS).map((value) => ({ value, label: monthOptionLabel(value, todayIso) })),
        [todayIso],
    );
    const period: StatementPeriod = useMemo(
        () => (mode === 'range' && appliedRange ? { kind: 'range', ...appliedRange } : { kind: 'month', month }),
        [mode, appliedRange, month],
    );
    const rangeError = validateRange(rangeDraft.from, rangeDraft.to, todayIso);

    const setMode = useCallback((next: StatementPeriodMode) => {
        setModeState(next);
        if (next === 'month') setAppliedRange(null);
    }, []);

    const applyRange = useCallback(() => {
        if (rangeError !== null || !rangeDraft.from || !rangeDraft.to) return;
        setAppliedRange({ from: rangeDraft.from, to: rangeDraft.to });
    }, [rangeError, rangeDraft]);

    return {
        todayIso,
        period,
        periodKey: periodKey(period),
        month,
        monthOptions,
        mode,
        rangeDraft,
        rangeError,
        setMode,
        selectMonth: setMonth,
        setRangeDraft,
        applyRange,
    };
}
